import type { JournalCandidateLink, DomainEventType } from '@steward/shared';

const money = (n: number) => `${n >= 0 ? '+' : ''}${n.toFixed(2)}`;
const FINISHED: Record<string, string> = { inside: 'inside', below_put: 'below put', above_call: 'above call' };

/**
 * The bot's side of a journal entry, for a chain that was opened from one of its candidates.
 *
 * Two shapes, because the interesting comparison changes with the event. On the way in it is
 * "what did the bot propose" (credit and short deltas) beside what the entry already says was
 * actually filled. On the way out it is the bot's theoretical hold-to-expiration result beside
 * Todd's realized one — the bot's selection quality versus his management of it, which are
 * different questions and must not be printed as one number.
 *
 * Props-driven by design (see CLAUDE.md, Web tests): the container owns the fetch.
 */
export function CandidateBotLine({
  link,
  eventType,
  realized,
}: {
  link: JournalCandidateLink | null;
  eventType: DomainEventType | null;
  /**
   * Realized chain P&L, per share and in dollars — the container computes it; null when unknown.
   * `JournalEntryCard` cannot know it without a chain fetch and passes null today, so the bot
   * side renders alone rather than the whole line disappearing.
   */
  realized: { perShare: number; dollars: number } | null;
}) {
  if (!link) return null;

  const closing = eventType === 'closed' || eventType === 'expired';
  const head = `Bot run ${link.runDate} · rank ${link.rank ?? '—'} · ${link.kind}`;

  if (!closing) {
    return (
      <div data-testid="bot-line" className="mt-1 text-xs text-gray-500">
        {head} · credit {link.botCredit.toFixed(2)} bot · short deltas{' '}
        {link.botShortPutDelta.toFixed(3)}/{link.botShortCallDelta.toFixed(3)} bot
      </div>
    );
  }

  const outcome = link.outcome;
  const bot =
    outcome && outcome.status === 'settled' && outcome.theoreticalPnl != null
      ? `Bot: ${money(outcome.theoreticalPnl)}/share theoretical, finished ${FINISHED[outcome.finished ?? ''] ?? '—'}`
      : outcome && outcome.status === 'unavailable'
        ? 'Bot: no close captured'
        : `Bot: settles ${link.expiration}`;
  const you = realized
    ? ` · You: ${money(realized.perShare)}/share, ${realized.dollars >= 0 ? '+' : '-'}$${Math.abs(realized.dollars).toFixed(0)}`
    : '';
  const rolled = link.rollCount > 0 ? ` · chain rolled ${link.rollCount}×` : '';

  return (
    <div data-testid="bot-line" className="mt-1 text-xs text-gray-500">
      {head} · {bot}
      {you}
      {rolled}
    </div>
  );
}
